import type { IClassDropdownItem } from '@/types';
import type { IAddTeacherPayload } from '@/types/payload';
import type { ITeacherDetails } from '@/types/teacher';

import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { IconEdit, IconPlus } from '@tabler/icons-react';

import CustomModal from '@/components/CustomModal';
import Input from '@/components/Input/Input';
import PhoneNumberInput from '@/components/phoneInput';
import Select from '@/components/Select';
import AddTeacherDrawerSkeleton from '@/components/SkeletonLoader/AddTeacherDrawerSkeleton';
import { Switch } from '@/components/Switch';
import { useFormatText } from '@/hooks/useFormatText';
import {
  addTeacherFormSchema,
  type TAddTeacherForm,
} from '@/schema/addTeacherSchema';
import { getClassDropdown } from '@/service/classService';
import { addTeacher, updateTeacher } from '@/service/teacherService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { zodResolver } from '@hookform/resolvers/zod';

interface IAddTeacherDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSuccess?: () => void;
  teacherToEdit?: ITeacherDetails | null;
  onClose?: () => void;
}

const AddTeacherDrawer = ({
  isOpen,
  setIsOpen,
  onSuccess,
  teacherToEdit,
  onClose,
}: IAddTeacherDrawerProps) => {
  const { formatText } = useFormatText();
  const { isDrawerLoading, setDrawerLoading } = useSkeletonStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [classOptions, setClassOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<TAddTeacherForm>({
    resolver: zodResolver(addTeacherFormSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      email: '',
      countryCode: '+49',
      phone_number: '',
      assignedClasses: [],
      sendCredentials: true,
    },
  });

  const countryCode = watch('countryCode');
  const isEditMode = Boolean(teacherToEdit);

  // Populate form when editing
  useEffect(() => {
    if (!isOpen) return;

    if (teacherToEdit) {
      reset({
        name: teacherToEdit.teacher_name || '',
        email: teacherToEdit.email || '',
        countryCode: teacherToEdit.phone_code || '+49',
        phone_number: teacherToEdit.phone_number || '',
        assignedClasses: teacherToEdit.classes?.map((c) => c.id) || [],
        sendCredentials: false, // Don't send credentials when editing
      });
    } else {
      reset({
        name: '',
        email: '',
        countryCode: '+49',
        phone_number: '',
        assignedClasses: [],
        sendCredentials: true,
      });
    }
  }, [teacherToEdit, isOpen, reset]);

  // Fetch class dropdown
  useEffect(() => {
    const fetchClassDropdown = async () => {
      setDrawerLoading(true);
      try {
        const res = await getClassDropdown(true);
        if (res?.data) {
          const options = res.data.map((item: IClassDropdownItem) => ({
            value: item.id,
            label: item.name,
          }));
          setClassOptions(options);
        }
      } finally {
        setDrawerLoading(false);
      }
    };

    if (isOpen) {
      fetchClassDropdown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    reset();
    setIsOpen(false);
    onClose?.();
  };

  const onSubmit = async (data: TAddTeacherForm) => {
    setIsSubmitting(true);

    try {
      const payload: IAddTeacherPayload = {
        name: data.name,
        phone_code: data.countryCode,
        phone_number: data.phone_number,
        class: data.assignedClasses ?? [],
      };

      if (!isEditMode) {
        payload.email = data.email;
        payload.send_email = data.sendCredentials ?? true;
      }

      let response;
      if (isEditMode && teacherToEdit?.id) {
        response = await updateTeacher(teacherToEdit.id, payload);
      } else {
        response = await addTeacher(payload);
      }

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        handleClose();
        onSuccess?.();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <CustomModal
      setIsOpen={handleClose}
      isHeader
      heading={formatText(
        isEditMode ? 'add-teacher.edit-title' : 'add-teacher.title'
      )}
      isFooter
      position="right"
      cancelButtonProps={{
        label: formatText('common.actions.cancel'),
        variant: 'link',
        onClick: handleClose,
      }}
      confirmButtonProps={{
        label: formatText(
          isEditMode ? 'add-teacher.edit-submit' : 'add-teacher.submit'
        ),
        variant: 'primary',
        icon: isEditMode ? <IconEdit size={18} /> : <IconPlus size={18} />,
        onClick: handleSubmit(onSubmit),
        loading: isSubmitting,
        disabled: isSubmitting,
      }}
    >
      {isDrawerLoading ? (
        <AddTeacherDrawerSkeleton />
      ) : (
        <form
          className="flex flex-col gap-5 w-full"
          onSubmit={handleSubmit(onSubmit)}
          method="post"
        >
          {/* Name */}
          <Input
            name="name"
            label={formatText('add-teacher.fields.name')}
            placeholder={formatText('add-teacher.fields.name-placeholder')}
            register={register}
            type="text"
            required
            error={
              errors.name?.message ? formatText(errors.name.message) : undefined
            }
            dataTestId="teacher-name-input"
          />

          {/* Email */}
          <Input
            name="email"
            label={formatText('add-teacher.fields.email')}
            placeholder={formatText('add-teacher.fields.email-placeholder')}
            register={register}
            type="email"
            required
            disabled={isEditMode}
            error={
              errors.email?.message
                ? formatText(errors.email.message)
                : undefined
            }
            dataTestId="teacher-email-input"
          />

          {/* Mobile Number */}
          <PhoneNumberInput
            name="phone_number"
            label={formatText('add-teacher.fields.mobile')}
            placeholder={formatText('add-teacher.fields.mobile-placeholder')}
            register={register}
            setValue={setValue}
            countryFlag
            required
            countrySelected={countryCode}
            error={
              errors.phone_number?.message
                ? formatText(errors.phone_number.message)
                : undefined
            }
            dataTestId="teacher-phone-input"
          />

          {/* Assign Class */}
          <Controller
            name="assignedClasses"
            control={control}
            render={({ field }) => (
              <Select
                name="assignedClasses"
                options={classOptions}
                selected={field.value || []}
                onChange={(val) => field.onChange(val)}
                placeholder={formatText('add-teacher.fields.assign-class')}
                isMulti
                isClearable
                dataTestId="assign-class-select"
              />
            )}
          />

          {/* Send Credentials Toggle */}
          <Controller
            name="sendCredentials"
            control={control}
            render={({ field }) => (
              <div
                className="flex items-center gap-3"
                data-testid="send-credentials-toggle"
              >
                <Switch
                  id="sendCredentials"
                  checked={field.value ?? true}
                  onChange={field.onChange}
                />
                <span className="text-sm font-medium text-neutral-primary">
                  {formatText('add-teacher.fields.send-credentials')}
                </span>
              </div>
            )}
          />
        </form>
      )}
    </CustomModal>
  );
};

export default AddTeacherDrawer;
